import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SkeletonText,
  Text,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import {
  adminFooterAvatarBoxProps,
  adminFooterAvatarIconProps,
  adminFooterControlProps,
  adminFooterNameProps,
  adminFooterProps,
  adminFooterSettingsButtonProps,
  adminHeaderSubtitleProps,
  adminNavItemProps,
  adminSectionLabelProps,
  adminSidebarLogoProps,
  adminSidebarPopoverProps,
  adminSidebarShellProps,
} from "../../../constants/adminSidebarTheme";
import { useAdminTheme } from "../../../context/AdminThemeContext";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
  FaBookOpen,
  FaFilePdf,
  FaQuestionCircle,
  FaRegFileAlt,
  FaEyeSlash,
  FaUserCircle,
  FaVideo,
  FaTags,
} from "react-icons/fa";
import { RiLogoutBoxRLine, RiPrinterFill, RiSettings4Line } from "react-icons/ri";
import { logoutUserAction } from "../../../modules/auth/Actions";
import { BsPeople, BsPersonX } from "react-icons/bs";
import { AiOutlineGroup } from "react-icons/ai";
import { LuDollarSign } from "react-icons/lu";
import {
  MdAccessTime,
  MdOutlineAssessment,
  MdOutlineAssignment,
  MdOutlineFactCheck,
  MdQuiz,
} from "react-icons/md";
import { BiPhone } from "react-icons/bi";
import AdminNotificationsBell from "../Notifications/AdminNotificationsBell";
import ThemeToggleButton from "../ThemeToggleButton";

const TeacherSidenavbar = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.user.isLoading);
  const location = useLocation();
  const { themeMode } = useAdminTheme();
  const isDark = themeMode === "dark";
  const footerControls = adminFooterControlProps(isDark);

  const sections = [
    {
      title: "Users",
      items: [
        { label: "Students", to: "/dashboard/students", icon: BsPeople },
        { label: "Centers", to: "/dashboard/lectures", icon: FaRegFileAlt },
        { label: "Groups", to: "/dashboard/groups", icon: AiOutlineGroup },
        { label: "Inactive", to: "/dashboard/inactive-students", icon: BsPersonX },
      ],
    },
    {
      title: "Curriculum",
      items: [
        { label: "Courses", to: "/dashboard/sections", icon: FaRegFileAlt },
        { label: "Lessons", to: "/dashboard/chapters", icon: FaBookOpen },
      ],
    },
    {
      title: "Content",
      items: [
        { label: "Sessions", to: "/dashboard/videos", icon: FaVideo },
        { label: "Live Sessions", to: "/dashboard/live-videos", icon: FaVideo },
        { label: "Office Hours", to: "/dashboard/office-hours", icon: MdAccessTime },
        { label: "Question Containers", to: "/dashboard/question-containers", icon: MdQuiz },
        { label: "Question Bank", to: "/dashboard/questions-bank", icon: MdQuiz },
        { label: "Exams", to: "/dashboard/exams", icon: MdOutlineAssignment },
        { label: "Homework", to: "/dashboard/homework", icon: MdOutlineAssignment },
        { label: "PDFs", to: "/dashboard/lessons", icon: FaFilePdf },
        { label: "Question Tags", to: "/dashboard/tags", icon: FaTags },
      ],
    },
    {
      title: "Finances",
      items: [
        { label: "Payments & Attendance", to: "/dashboard/payments", icon: LuDollarSign },
        { label: "Payment Subjects", to: "/dashboard/payment-subjects", icon: FaRegFileAlt },
      ],
    },
    {
      title: "Staff",
      items: [
        { label: "Teaching Assistants", to: "/dashboard/assistants", icon: BiPhone },
        { label: "Assistant Phones", to: "/dashboard/subject-assist-phones", icon: BiPhone },
      ],
    },
    {
      title: "Requests",
      items: [
        {
          label: "Exam Redo Requests",
          to: "/dashboard/exam-redo-requests",
          icon: FaQuestionCircle,
        },
        {
          label: "Video More View Requests",
          to: "/dashboard/videos-more-view-requests",
          icon: FaEyeSlash,
        },
      ],
    },
    {
      title: "Reports",
      items: [
        {
          label: "Review Homeworks",
          to: "/dashboard/uncomment-homework-report",
          icon: MdOutlineAssessment,
        },
        {
          label: "HW Comprehensive Report",
          to: "/dashboard/homework-report-by-chapter",
          icon: MdOutlineFactCheck,
        },
        {
          label: "Live video attendance",
          to: "/dashboard/live-video-attendance-report",
          icon: FaVideo,
        },
        {
          label: "Detailed exam report",
          to: "/dashboard/detailed-exam-report",
          icon: MdQuiz,
        },
        {
          label: "Center exam grading",
          to: "/dashboard/attendence-simple-report",
          icon: MdOutlineFactCheck,
        },
        {
          label: "Center exam marks",
          to: "/dashboard/manual-exam-marks-report",
          icon: MdOutlineAssessment,
        },
        {
          label: "Print Questions",
          to: "/dashboard/print-all-questions",
          icon: RiPrinterFill,
        },
      ],
    },
  ];

  return (
    <Flex
      as="aside"
      data-admin-sidebar=""
      position={embedded ? "relative" : "fixed"}
      left={embedded ? undefined : "0"}
      top={embedded ? undefined : "0"}
      h={embedded ? "100%" : "100vh"}
      minH={embedded ? "min(100vh, 720px)" : undefined}
      w={embedded ? "100%" : "260px"}
      maxW={embedded ? "100%" : undefined}
      {...adminSidebarShellProps(isDark)}
      flexDirection="column"
      zIndex={embedded ? 1 : "40"}
      overflow="hidden"
    >
      <Box px={6} py={8}>
        <Image
          {...adminSidebarLogoProps(isDark)}
          onClick={() => window.location.replace("/")}
        />
        <Text {...adminHeaderSubtitleProps(isDark)} mt={4} textAlign="center">
          Admin System
        </Text>
      </Box>

      {isLoading ? (
        <HStack w="100%" bg="transparent" h="100%">
          <SkeletonText
            startColor="gray.100"
            endColor="gray.600"
            w="80%"
            mt="4"
            noOfLines={7}
            spacing="20"
          />
        </HStack>
      ) : (
        <Box
          flex="1"
          overflowY="auto"
          px={4}
          pb={20}
          className="scroll"
          sx={{
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE/Edge legacy
            "&::-webkit-scrollbar": {
              display: "none", // Chrome/Safari
            },
          }}
        >
          {sections.map((section) => (
            <Box key={section.title} mb={6}>
              <Text {...adminSectionLabelProps(isDark)}>{section.title}</Text>
              <Box>
                {section.items.map((item) => {
                  const active = location.pathname === item.to;
                  const IconCmp = item.icon;
                  return (
                    <Link key={item.to} to={item.to} style={{ textDecoration: "none" }}>
                      <Flex {...adminNavItemProps(active, isDark)}>
                        <Box as={IconCmp} fontSize="18px" />
                        <Text flex="1" textAlign="left">
                          {item.label}
                        </Text>
                      </Flex>
                    </Link>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Flex {...adminFooterProps(isDark)} align="center" gap={3} justify="space-between">
        <Flex align="center" gap={3} minW={0} flex="1">
          <Flex {...adminFooterAvatarBoxProps(isDark)}>
            <Box as={FaUserCircle} {...adminFooterAvatarIconProps(isDark)} />
          </Flex>
          <Box overflow="hidden">
            <Text {...adminFooterNameProps(isDark)}>Admin Profile</Text>
          </Box>
        </Flex>

        <AdminNotificationsBell {...footerControls.bell} />
        <ThemeToggleButton {...footerControls.themeToggle} />
        <Popover placement="top-end" closeOnBlur>
          <PopoverTrigger>
            <IconButton
              aria-label="Account settings"
              icon={<RiSettings4Line size={22} />}
              {...adminFooterSettingsButtonProps(isDark)}
            />
          </PopoverTrigger>
          <PopoverContent
            w="220px"
            border="1px solid"
            {...adminSidebarPopoverProps(isDark)}
            bg="var(--surface-raised)"
            boxShadow="lg"
            _focus={{ outline: "none" }}
          >
            <PopoverArrow
              bg="var(--surface-raised)"
              borderColor={adminSidebarPopoverProps(isDark).borderColor}
            />
            <PopoverBody p={3}>
              <Button
                w="100%"
                justifyContent="flex-start"
                leftIcon={<RiLogoutBoxRLine size={20} />}
                bg={brandPalette.primary}
                color="white"
                size="sm"
                fontWeight="700"
                borderRadius="lg"
                _hover={{ bg: brandPalette.secondary }}
                onClick={() => dispatch(logoutUserAction())}
              >
                Log out
              </Button>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>
    </Flex>
  );
};

export default TeacherSidenavbar;
